import Image from "@/components/common/ImageWithFallback";
import Link from "next/link";
import { twMerge } from "tailwind-merge";
import { useIsDark } from "@/context/ThemeContext";

export default function BlogBanner({
  image,
  blurDataURL,
  author,
  authorSlug,
  authorImage = "/assets/blog/profileAvatar.svg",
  date,
  title,
  description,
  slug,
}) {
  const isdark = useIsDark();

  const postUrl = `/blog/${slug}`;
  const descriptiveText = `Read more about ${title}`;

  return (
    <div className="group relative max:w-[1242px] sm:min-h-[312px] laptop:min-h-[314px] bg-card rounded-[8px] mt-[30px] mobile:mt-[50px] md:mt-[20px] sm:flex tablet:flex-row flex-col overflow-hidden border border-card max-w-[1800px] mx-auto">
      {/* Image */}
      <div className="tablet:w-[952px] w-full laptop:min-h-[314px] aspect-20/9 h-auto overflow-hidden relative">
        <Link href={`/blog/${slug}`}>
          <Image
            src={image}
            alt={title}
            fill
            sizes="(max-width: 640px) 100vw,
                 1100px"
            quality={100}
            fetchPriority="high"
            priority
            placeholder={blurDataURL ? "blur" : "empty"}
            blurDataURL={blurDataURL}
            fallbackSrc="/assets/fallbacks/blogFallback.webp"
            className="object-cover transform transition-transform duration-700 ease-out group-hover:scale-110 sm:group-hover:-translate-x-9 sm:group-hover:-translate-y-2 cursor-pointer"
          />
        </Link>
      </div>

      {/* Content */}
      <div className="relative tablet:w-[738px] 3xl:w-[900px] w-full laptop-h-[310px] h-auto overflow-hidden">
        <div className="tablet:absolute tablet:inset-0 p-4 tablet:p-6 laptop:p-8 3xl:p-10  flex flex-col tablet:justify-between justify-start bg-card z-20">
          {/* Author + Date */}
          <div className="flex items-center gap-3 text-sm text-gray-400 mb-3 tablet:mb-0">
            {authorSlug ? (
              <Link href={`/blog/author/${authorSlug}`}>
                <div className="w-10 h-10 sm:w-14 sm:h-14 tablet:w-10 tablet:h-10 3xl:w-14 3xl:h-14 rounded-full overflow-hidden flex-shrink-0 relative cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all duration-200">
                  <Image
                    src={authorImage}
                    alt={author}
                    fill
                    sizes="100px"
                    quality={70}
                    fallbackSrc="/assets/fallbacks/avatar-fallback.svg"
                    className="object-cover"
                  />
                </div>
              </Link>
            ) : (
              <div className="w-10 h-10 3xl:w-14 3xl:h-14 rounded-full overflow-hidden flex-shrink-0 relative">
                <Image
                  src={authorImage}
                  alt={author}
                  fill
                  sizes="100px"
                  quality={70}
                  fallbackSrc="/assets/fallbacks/avatar-fallback.svg"
                  className="object-cover"
                />
              </div>
            )}
            <div className="flex flex-col sm:gap-1 sm:gap-2 tablet:gap-0 3xl:gap-2">
              {authorSlug ? (
                <Link href={`/blog/author/${authorSlug}`}>
                  <p className="font-manrope font-medium text-[14px] sm:text-[16px]  tablet:text-[14px] 3xl:text-[18px] 4xl:text-[22px]  leading-[21px] tracking-[0.15px] text-foreground cursor-pointer hover:underline transition-all duration-200">
                    {author}
                  </p>
                </Link>
              ) : (
                <p className="font-manrope font-medium text-[14px] sm:text-[16px]  tablet:text-[14px] 3xl:text-[18px] 4xl:text-[22px] leading-[21px] tracking-[0.15px] text-foreground">
                  {author}
                </p>
              )}
              <p className="font-manrope font-medium text-[12px] sm:text-[14px]  tablet:text-[12px] 3xl:text-[16px] 4xl:text-[20px] leading-[21px] tracking-[0.15px] dark:text-[#909090] text-[#5A5A6E]">
                {date}
              </p>
            </div>
          </div>

          {/* Title */}
          <p>
            <Link
              href={`/blog/${slug}`}
              title={title}
              className={twMerge(
                "font-alexandria font-normal text-[18px] sm:text-[22px] lg:text-[22px] laptop:text-[22px] 3xl:text-[26px] 4xl:text-[30px] leading-[28px] md:leading-[30px] laptop:leading-[36px] 3xl:leading-[38px] 4xl:leading-40px]  tracking-[0.15px] 3xl:tracking-[0.18px] 4xl:tracking-[0.20px] sm:mt-2 mb-3 sm:mb-0 bg-clip-text text-transparent line-clamp-2 sm:line-clamp-2 tablet:line-clamp-2 transition-all duration-300 cursor-pointer",
                isdark
                  ? "bg-gradient-to-r from-[#FF1CF7] via-[#FF1CF7] to-[#EEEEFF]"
                  : "bg-gradient-to-r from-[#9C00BB] to-[#1A1A1A]"
              )}
            >
              {title}
            </Link>
          </p>

          {/* Description */}
          <p
            title={description}
            className="font-manrope font-normal text-[14px] sm:text-[16px] lg:text-[16px] laptop:text-[16px] 3xl:text-[20px] 4xl:text-[24px] leading-[25px] laptop:leading-[28px] laptop:tracking-[0.15px] 3xl:leading-[30px] 3xl:tracking-[0.17px] 4xl:leading-[32px] 4xl:tracking-[0.20px] dark:text-[#909090] text-[#5A5A6E] sm:mt-2 mb-4 sm:mb-0 line-clamp-4 lg:line-clamp-3 laptop:line-clamp-3 2xl:line-clamp-4 3xl:line-clamp-3 4xl:line-clamp-4 transition-all duration-300"
          >
            {description}
          </p>

          <div className="sm:mt-4 mt-0">
            <Link
              href={postUrl}
              aria-label={descriptiveText}
              className="flex items-center gap-2 text-foreground font-alexandria font-normal lg:text-[16px] laptop:text-[16px] 3xl:text-[20px] 4xl:text-[24px] hover:underline"
            >
              Read more{" "}
              <Image
                src={
                  isdark
                    ? "/assets/blog/arrowdark.svg"
                    : "/assets/blog/arrowlight.svg"
                }
                alt="arrow"
                width={18}
                height={18}
                className="inline-block"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
